import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ProjectHeader from './ProjectHeader';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import {
  LABELS, analyze, capturedRows, createExpected, datasetFingerprint, emptyFilters, expectedRow,
  exportAudit, hasSideFilters, loadDocument, locationLabel, matchRows, originMatches,
  parseExpectedCSV, partIdentifiers, primaryIdentifier, quantityAgrees, restoreBackup, reviewMatches, rowMatches, savePayload,
  setGroupNote, sortGroups, toggleConfirmed, total, unmatchGroup
} from '../utils/partsAudit';
import './PartsComparisonTool.css';

const PAGE_SIZE = 50;
const SIDE_LABELS = { left: 'Expected / ERP', right: 'Captured / Reshoot' };
const display = value => value === '' || value === null || value === undefined ? '—' : String(value);
const statusClass = group => group.manual ? 'exact' : group.status;
const download = (name, contents, type) => {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = name; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const readFile = async file => {
  if (file.size > 10 * 1024 * 1024) throw new Error('Choose a file smaller than 10 MB.');
  return file.text();
};

export function AuditTable({ side, entries, selected = new Set(), onSelect, onOpen, onSearch, readOnly = false, detail = false }) {
  const columns = side === 'left'
    ? [['location', 'Location'], ['datecode', 'Date Code'], ['manufacturer', 'Manufacturer'], ['quantity', 'Quantity'], ['pn', 'Part Number']]
    : [['pn', 'MPN'], ['secondary', 'Secondary PN'], ['quantity', 'Quantity'], ['manufacturer', 'Manufacturer'],
      ['datecode', 'Date Code'], ['image0', '1'], ['image1', '2'], ['image2', '3'], ['location', 'Location']];
  return (
    <Table size="sm" className={`audit-table mb-0 ${detail ? 'audit-detail-table' : ''}`} aria-label={`${SIDE_LABELS[side]} inventory`}>
      <thead><tr>{columns.map(([key, label]) => <th key={key} scope="col">{label}</th>)}</tr></thead>
      <tbody>
        {!entries.length && <tr><td colSpan={columns.length} className="text-muted text-center">No rows match these filters.</td></tr>}
        {entries.map(({ row, group, key }, index) => !row ? (
          <tr className="audit-padding" aria-hidden="true" key={key || `blank-${index}`}><td colSpan={columns.length}>&nbsp;</td></tr>
        ) : (
          <tr key={row.id} className={`audit-status-${statusClass(group)} ${selected.has(row.id) ? 'audit-selected' : ''}`}
            tabIndex={detail ? undefined : 0} aria-label={detail ? undefined : `Review ${side} ${primaryIdentifier(row) || row.id}`}
            onKeyDown={event => {
              if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
                event.preventDefault(); onOpen?.(group);
              }
            }} onClick={() => onOpen?.(group)}>
            {columns.map(([field]) => (
              <td key={field} title={field === 'location' ? locationLabel(row) : display(row[field])}>
                {field.startsWith('image') ? (
                  row.images[Number(field.slice(5))] ? (
                    <a href={row.images[Number(field.slice(5))]} target="_blank" rel="noopener noreferrer"
                      onClick={event => event.stopPropagation()} aria-label={`Image ${Number(field.slice(5)) + 1} for ${row.pn}`}>View</a>
                  ) : null
                ) : field === 'pn' ? (
                  <div className="audit-part-cell">
                    {!detail && <Form.Check type="checkbox" checked={selected.has(row.id)}
                      disabled={readOnly || group.manual} aria-label={`Select ${side} ${primaryIdentifier(row) || row.id}`}
                      title={group.manual ? 'Unmatch this saved allocation before reassigning its rows' : 'Select for manual match'}
                      onClick={event => event.stopPropagation()} onChange={() => onSelect?.(row.id)} />}
                    <Button variant="link" size="sm" className="audit-pn" title={`Search the opposite side. Identifiers: ${partIdentifiers(row).join(', ')}`}
                      disabled={!primaryIdentifier(row)}
                      onClick={event => { event.stopPropagation(); onSearch?.(primaryIdentifier(row)); }}>{primaryIdentifier(row) || 'No part identifier'}</Button>
                    {group.confirmed && <span className="audit-reviewed" title="Review confirmed" aria-label="Review confirmed">✓</span>}
                  </div>
                ) : field === 'secondary' && row.secondary ? (
                  partIdentifiers({ secondary: row.secondary }).map((identifier, aliasIndex) => <React.Fragment key={identifier}>
                    {aliasIndex > 0 && ', '}
                    <Button variant="link" size="sm" className="audit-pn" onClick={event => {
                      event.stopPropagation(); onSearch?.(identifier);
                    }}>{identifier}</Button>
                  </React.Fragment>)
                ) : field === 'location' ? display(locationLabel(row)) : display(row[field])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function SideFilters({ side, rows, filter, onChange, onClear, counts }) {
  const [locationSearch, setLocationSearch] = useState('');
  const locations = useMemo(() => {
    const values = new Map();
    rows.forEach(row => {
      if (row.location) {
        if (!values.has(row.location)) values.set(row.location, new Set());
        values.get(row.location).add(locationLabel(row));
      }
    });
    return [...values].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rows]);
  const set = (field, value) => onChange({ ...filter, [field]: value });
  return (
    <div className="audit-side-controls">
      <div className="audit-field-filters">
        {[['pn', 'Part number / alias'], ['quantity', 'Quantity (exact)'], ['manufacturer', 'Manufacturer'], ['datecode', 'Date code']].map(([field, label]) => (
          <Form.Control key={field} size="sm" aria-label={`${SIDE_LABELS[side]} ${label}`} placeholder={field === 'pn' ? 'MPN / IPN / part number / aliases' : label}
            value={filter[field]} onChange={event => set(field, event.target.value)} />
        ))}
      </div>
      <div className="d-flex gap-2 mt-2">
        <Form.Control size="sm" placeholder="Find location…" aria-label={`${SIDE_LABELS[side]} find location`}
          value={locationSearch} onChange={event => setLocationSearch(event.target.value)} />
        <Form.Select size="sm" value={filter.location} aria-label={`${SIDE_LABELS[side]} location`}
          onChange={event => set('location', event.target.value)}>
          <option value="">All locations</option>
          {locations.filter(([name]) => name === filter.location || name.toLowerCase().includes(locationSearch.toLowerCase()))
            .map(([name, paths]) => <option value={name} key={name}>{[...paths].join(' / ')}</option>)}
        </Form.Select>
      </div>
      <div className="audit-status-filters mt-2" role="group" aria-label={`${SIDE_LABELS[side]} status filters`}>
        <Button size="sm" variant={!filter.statuses.length ? 'primary' : 'outline-secondary'} aria-pressed={!filter.statuses.length}
          onClick={() => set('statuses', [])}>All parts</Button>
        {Object.entries(LABELS).map(([status, label]) => (
          <Button key={status} size="sm" variant="light" className={`audit-status-button audit-status-${status} ${filter.statuses.includes(status) ? 'active' : ''}`}
            aria-pressed={filter.statuses.includes(status)} onClick={() => set('statuses',
              filter.statuses.includes(status) ? filter.statuses.filter(value => value !== status) : [...filter.statuses, status])}>
            {label} <span className="audit-filter-count">{counts[status] || 0}</span>
          </Button>
        ))}
      </div>
      <Button size="sm" variant="link" className="px-0 mt-1" onClick={() => { setLocationSearch(''); onClear(); }}>Clear {SIDE_LABELS[side]} filters</Button>
    </div>
  );
}

function Pager({ page, count, onChange, label }) {
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE)), bounded = Math.min(page, pages - 1);
  return (
    <div className="audit-pager" aria-label={`${label} pagination`}>
      <span>{count.toLocaleString()} {label} · page {bounded + 1} of {pages}</span>
      <div className="d-flex gap-1">
        <Button size="sm" variant="outline-secondary" disabled={bounded === 0} onClick={() => onChange(bounded - 1)} aria-label={`Previous ${label}`}>Previous</Button>
        <Button size="sm" variant="outline-secondary" disabled={bounded + 1 >= pages} onClick={() => onChange(bounded + 1)} aria-label={`Next ${label}`}>Next</Button>
      </div>
    </div>
  );
}
const pageSlice = (rows, page) => rows.slice(Math.min(page, Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1)) * PAGE_SIZE,
  (Math.min(page, Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1)) + 1) * PAGE_SIZE);

function PartsComparisonTool() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null), [document, setDocument] = useState(null), [right, setRight] = useState([]);
  const [loading, setLoading] = useState(true), [loadError, setLoadError] = useState(''), [message, setMessage] = useState(null);
  const [canEdit, setCanEdit] = useState(false), [saving, setSaving] = useState(false), [dirty, setDirty] = useState(false);
  const [backupReady, setBackupReady] = useState(false);
  const [filters, setFilters] = useState({ left: emptyFilters(), right: emptyFilters() });
  const [filterReset, setFilterReset] = useState({ left: 0, right: 0 });
  const [method, setMethod] = useState(''), [review, setReview] = useState(''), [category, setCategory] = useState('');
  const [sort, setSort] = useState('source'), [direction, setDirection] = useState('asc');
  const [pages, setPages] = useState({ left: 0, right: 0, group: 0 });
  const [selected, setSelected] = useState({ left: new Set(), right: new Set() });
  const [manualNote, setManualNote] = useState(''), [modalId, setModalId] = useState(null), [groupNote, setNote] = useState('');
  const [confirmation, setConfirmation] = useState(null), [importPreview, setImportPreview] = useState(null);
  const savingRef = useRef(false), revision = useRef(0), routeRef = useRef(projectId), editRef = useRef(false);
  const scrollPanes = useRef({ left: null, right: null });
  routeRef.current = projectId;
  const userId = user?.user_id || user?.userId || user?.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError(''); setCanEdit(false); editRef.current = false;
    setDocument(null); setDirty(false); setMessage(null);
    setSaving(false); setBackupReady(false);
    setSelected({ left: new Set(), right: new Set() }); setModalId(null);
    setFilters({ left: emptyFilters(), right: emptyFilters() }); setPages({ left: 0, right: 0, group: 0 });
    setConfirmation(null); setImportPreview(null); setMethod(''); setReview(''); setCategory('');
    async function load() {
      try {
        const [projectResponse, partsResponse] = await Promise.all([
          apiService.api.get(`/project/${projectId}`, { params: { fetchExpected: true } }),
          apiService.getAllPartsForProject(projectId, { includeImages: true })
        ]);
        if (cancelled) return;
        const rows = capturedRows(partsResponse.data.parts || [], partsResponse.data);
        if (new Set(rows.map(row => row.id)).size !== rows.length || rows.some(row => !row.id)) {
          throw new Error('Captured inventory contains missing or duplicate part IDs.');
        }
        const next = loadDocument(projectResponse.data.expectedLineItems, rows);
        setProject(projectResponse.data); setRight(rows); setDocument(next);
        const allowed = Boolean(userId && String(partsResponse.data.projectOwnerId) === String(userId));
        setCanEdit(allowed); editRef.current = allowed;
        // Persist generated row IDs/migrated matches before allowing portable review backups.
        let original = projectResponse.data.expectedLineItems;
        for (let i = 0; typeof original === 'string' && i < 3; i += 1) original = JSON.parse(original);
        setBackupReady(Boolean(original?.audit && original.lineItems?.every(item => item.lineItemId)));
        setDirty(Boolean(allowed && (!original?.audit || original.lineItems?.some(item => !item.lineItemId))));
      } catch (error) {
        if (!cancelled) setLoadError(error.message || 'Failed to load project data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; editRef.current = false; };
  }, [projectId, userId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const left = useMemo(() => document?.lineItems.map(expectedRow) || [], [document]);
  const groups = useMemo(() => document ? analyze(left, right, document.audit) : [], [left, right, document]);
  const filteredGroups = useMemo(() => sortGroups(groups.filter(group =>
    (!category || group.status === category) && originMatches(group, method) && reviewMatches(group, review)), sort, direction),
  [groups, category, method, review, sort, direction]);
  const independent = hasSideFilters(filters.left) || hasSideFilters(filters.right);
  const sideEntries = useMemo(() => ({
    left: filteredGroups.flatMap(group => group.leftRows.filter(row => rowMatches(row, group, filters.left)).map(row => ({ row, group }))),
    right: filteredGroups.flatMap(group => group.rightRows.filter(row => rowMatches(row, group, filters.right)).map(row => ({ row, group })))
  }), [filteredGroups, filters]);
  const shown = useMemo(() => {
    if (independent) return { left: pageSlice(sideEntries.left, pages.left), right: pageSlice(sideEntries.right, pages.right) };
    const result = { left: [], right: [] };
    for (const group of pageSlice(filteredGroups, pages.group)) {
      for (let index = 0; index < Math.max(group.left.length, group.right.length); index += 1) {
        for (const side of ['left', 'right']) result[side].push({
          row: group[`${side}Rows`][index], group, key: `${group.id}-${side}-${index}`
        });
      }
    }
    return result;
  }, [independent, sideEntries, pages, filteredGroups]);
  const counts = useMemo(() => {
    const result = { groups: {}, left: {}, right: {} };
    groups.forEach(group => {
      result.groups[group.status] = (result.groups[group.status] || 0) + 1;
      for (const side of ['left', 'right']) result[side][statusClass(group)] = (result[side][statusClass(group)] || 0) + group[side].length;
    });
    return result;
  }, [groups]);
  const activeGroup = groups.find(group => group.id === modalId);
  const selectedLeft = left.filter(row => selected.left.has(row.id)), selectedRight = right.filter(row => selected.right.has(row.id));
  const editable = canEdit && !saving;
  const missingLocations = left.some(row => !row.location);
  const reportError = error => setMessage({ variant: 'danger', text: error.response?.data?.error || error.message || 'The operation failed.' });
  const resetPages = () => setPages({ left: 0, right: 0, group: 0 });
  const clearFilters = () => {
    setFilters({ left: emptyFilters(), right: emptyFilters() }); setMethod(''); setReview(''); setCategory(''); resetPages();
    setFilterReset(previous => ({ left: previous.left + 1, right: previous.right + 1 }));
  };
  const updateFilter = (side, value) => {
    setFilters(previous => ({ ...previous, [side]: value }));
    setPages(previous => ({ ...previous, [side]: 0, group: 0 }));
  };
  const searchOpposite = (side, pn) => {
    const target = side === 'left' ? 'right' : 'left';
    setFilters(previous => ({ ...previous, [target]: { ...emptyFilters(), pn: String(pn) } }));
    setFilterReset(previous => ({ ...previous, [target]: previous[target] + 1 }));
    setMethod(''); setReview(''); setCategory(''); resetPages(); setModalId(null);
  };
  const pick = (side, id) => {
    if (!editRef.current || savingRef.current || routeRef.current !== projectId) return;
    setSelected(previous => {
      const next = new Set(previous[side]);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...previous, [side]: next };
    });
  };
  const openGroup = group => { setModalId(group.id); setNote(group.note || ''); };

  async function save(next = document) {
    if (!editRef.current || savingRef.current || !next || routeRef.current !== projectId) return;
    const requestProjectId = projectId, requestRevision = revision.current;
    savingRef.current = true; setSaving(true); setMessage(null);
    try {
      const payload = savePayload(next, analyze(next.lineItems.map(expectedRow), right, next.audit));
      const serialized = JSON.stringify(payload);
      if (new Blob([serialized]).size > 350 * 1024) {
        throw new Error('This audit exceeds the project storage allowance (350 KB). Your changes remain unsaved; download an audit CSV before leaving.');
      }
      await apiService.updateProject(projectId, { expectedLineItems: serialized });
      if (routeRef.current === requestProjectId && revision.current === requestRevision) {
        setDirty(false); setBackupReady(true); setMessage({ variant: 'success', text: 'Audit saved to this project.' });
      }
    } catch (error) {
      if (routeRef.current === requestProjectId) { setDirty(true); reportError(error); }
    } finally {
      savingRef.current = false;
      if (routeRef.current === requestProjectId) setSaving(false);
    }
  }
  async function commit(next) {
    if (!editRef.current || savingRef.current || routeRef.current !== projectId) return;
    revision.current += 1;
    setDocument(next); setDirty(true);
    await save(next);
  }
  const mutate = async operation => {
    if (!editRef.current || savingRef.current || routeRef.current !== projectId) return;
    try { await commit(operation()); } catch (error) { reportError(error); }
  };
  const ask = (title, body, action) => {
    if (editable) setConfirmation({ title, body, action });
  };
  const clearSelection = () => { setSelected({ left: new Set(), right: new Set() }); setManualNote(''); };
  const confirmMatch = () => {
    const action = () => mutate(() => {
      const next = matchRows(document, right, [...selected.left], [...selected.right], manualNote);
      clearSelection();
      return next;
    });
    if (!quantityAgrees(selectedLeft, selectedRight)) {
      ask('Match different quantities?', `Expected total: ${display(total(selectedLeft))}. Captured total: ${display(total(selectedRight))}. This creates an explicit manual match without changing either quantity.`, action);
    } else action();
  };
  async function backup() {
    try {
      const fingerprint = await datasetFingerprint(left, right);
      download(`audit-review-${projectId}.json`, JSON.stringify({ version: 1, projectId, fingerprint, audit: document.audit }, null, 2), 'application/json');
    } catch (error) { reportError(error); }
  }
  async function restore(file) {
    if (!file || !editable) return;
    const requestRevision = revision.current;
    try {
      const backupData = JSON.parse(await readFile(file)), fingerprint = await datasetFingerprint(left, right);
      if (routeRef.current !== projectId || !editRef.current) return;
      if (revision.current !== requestRevision) throw new Error('The audit changed while reading this backup. Choose the backup again.');
      const next = restoreBackup(backupData, projectId, fingerprint, document, right);
      ask('Restore review backup?', 'This replaces this project’s current allocations, notes, and confirmations for the same dataset.', () => mutate(() => next));
    } catch (error) { reportError(error); }
  }
  async function importCSV(file) {
    if (!file || !editable) return;
    try {
      const next = parseExpectedCSV(await readFile(file));
      if (routeRef.current === projectId && editRef.current) setImportPreview({ name: file.name, document: next });
    } catch (error) { reportError(error); }
  }
  const exportCSV = side => {
    try { download(`${side === 'left' ? 'enriched-expected' : 'captured-audit'}-${projectId}.csv`, exportAudit(side === 'left' ? left : right, groups, side), 'text/csv;charset=utf-8'); }
    catch (error) { reportError(error); }
  };

  if (loading) return <Container className="py-5 text-center"><Spinner animation="border" role="status" /><p className="mt-3">Loading inventory audit…</p></Container>;
  if (loadError) return <Container className="py-4"><Alert variant="danger">{loadError}</Alert><Button href={`/project/${projectId}/allparts`}>Back to Parts List</Button></Container>;
  return (
    <Container fluid className="py-4 ic-container parts-audit">
      <div className="audit-project-header"><ProjectHeader project={project} projectId={projectId}
        leftButtons={[{ text: 'Back to Parts List', icon: faArrowLeft, destinationUrl: `/project/${projectId}/allparts` }]}
        showAddLocation={false} onProjectUpdate={setProject} onShowToast={text => setMessage({ variant: 'info', text })}
        userCanEdit={canEdit} isPartComparisonPage /></div>
      <div className="audit-heading">
        <div><h2>Inventory audit</h2><p className="text-muted mb-0">Compare expected inventory with captured parts. Review a row to inspect its entire match group.</p></div>
        <div className="d-flex align-items-center gap-2">
          <Badge bg={dirty ? 'warning' : 'success'} text={dirty ? 'dark' : 'white'}>{dirty ? 'Unsaved changes' : 'Project saved'}</Badge>
          {canEdit && <Button variant="success" disabled={!dirty || saving} onClick={() => save()}>{saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save audit'}</Button>}
        </div>
      </div>
      {message && <Alert variant={message.variant} dismissible onClose={() => setMessage(null)} className="mt-3" role="status">{message.text}</Alert>}
      {!canEdit && <Alert variant="info" className="mt-3">Read-only audit. Only the project owner can change matches, import inventory, or confirm reviews.</Alert>}
      {missingLocations && <Alert variant="light" className="mt-3 border">Some expected rows have no location. Related parts remain in Needs Review unless explicitly matched; a missing location is never assumed to agree.</Alert>}
      <div className="audit-summary" aria-label="Match categories">
        {Object.entries(LABELS).map(([status, label]) => <button key={status} type="button"
          className={`audit-summary-card audit-status-${status} ${category === status ? 'active' : ''}`}
          aria-pressed={category === status} onClick={() => { setCategory(category === status ? '' : status); resetPages(); }}>
          <strong>{counts.groups[status] || 0}</strong><span>{label}</span>
        </button>)}
      </div>
      <Card className="mb-3"><Card.Body className="audit-toolbar">
        <div className="d-flex gap-2 flex-wrap">
          <Button size="sm" variant="outline-primary" onClick={() => exportCSV('left')}>Export enriched expected</Button>
          <Button size="sm" variant="outline-primary" onClick={() => exportCSV('right')}>Export captured audit</Button>
          <Button size="sm" variant="outline-secondary" disabled={dirty || saving || !backupReady} onClick={backup} title={dirty || !backupReady ? 'Save the project first so backup row IDs are durable' : 'Download review decisions'}>Back up review</Button>
        </div>
        {canEdit && <div className="d-flex gap-2 flex-wrap">
          <label className={`btn btn-sm btn-outline-primary mb-0 ${!editable ? 'disabled' : ''}`}>
            Import Expected CSV
            <input className="visually-hidden" type="file" accept=".csv,text/csv" disabled={!editable} onChange={event => {
              importCSV(event.target.files[0]); event.target.value = '';
            }} aria-label="Import Expected CSV" />
          </label>
          <label className={`btn btn-sm btn-outline-secondary mb-0 ${!editable ? 'disabled' : ''}`}>
            Restore review
            <input className="visually-hidden" type="file" accept=".json,application/json" disabled={!editable} onChange={event => {
              restore(event.target.files[0]); event.target.value = '';
            }} aria-label="Restore review" />
          </label>
        </div>}
      </Card.Body></Card>
      <div className="audit-shared-filters">
        <Form.Select size="sm" aria-label="Match method" value={method} onChange={event => { setMethod(event.target.value); resetPages(); }}>
          <option value="">All match methods</option><option value="manual">Manual / saved matches</option>
          <option value="automatic">Automatic matches</option><option value="unmatched">Unmatched / Needs Review</option><option value="created">Created from captured</option>
        </Form.Select>
        <Form.Select size="sm" aria-label="Review status" value={review} onChange={event => { setReview(event.target.value); resetPages(); }}>
          <option value="">All review statuses</option><option value="confirmed">Confirmed</option><option value="unconfirmed">Unconfirmed</option>
        </Form.Select>
        <Form.Select size="sm" aria-label="Sort by location" value={sort} onChange={event => { setSort(event.target.value); resetPages(); }}>
          <option value="source">Source group order</option><option value="expected">Expected location</option>
          <option value="captured">Captured location</option><option value="both">Both · expected then captured</option>
        </Form.Select>
        <Form.Select size="sm" aria-label="Sort direction" value={direction} onChange={event => { setDirection(event.target.value); resetPages(); }}>
          <option value="asc">A → Z</option><option value="desc">Z → A</option>
        </Form.Select>
        <Button size="sm" variant="outline-secondary" onClick={clearFilters}>Clear all filters</Button>
      </div>
      {canEdit && <Card className="my-3 audit-manual"><Card.Body>
        <div className="audit-toolbar">
          <div><strong>Manual match</strong><div className="small text-muted">{selected.left.size} expected / {selected.right.size} captured selected · totals {display(total(selectedLeft))} / {display(total(selectedRight))}</div></div>
          <Form.Control size="sm" className="audit-manual-note" placeholder="Optional match note" aria-label="Manual match note"
            value={manualNote} disabled={!editable} onChange={event => setManualNote(event.target.value)} />
          <div className="d-flex gap-2 flex-wrap">
            <Button size="sm" variant="success" disabled={!editable || !selected.left.size || !selected.right.size} onClick={confirmMatch}>Match selected</Button>
            <Button size="sm" variant="outline-primary" disabled={!editable || selected.left.size > 0 || !selected.right.size} onClick={() =>
              ask('Create expected inventory?', `Add ${selected.right.size} expected rows linked to the selected captured parts. Original captured parts will not be changed.`, () => mutate(() => {
                const next = createExpected(document, right, [...selected.right], manualNote); clearSelection(); return next;
              }))}>Create expected rows</Button>
            <Button size="sm" variant="outline-secondary" disabled={saving} onClick={clearSelection}>Clear selection</Button>
          </div>
        </div>
        <p className="small text-muted mb-0 mt-2">Selections survive filtering and pagination. Saved manual allocations must be unmatched before their rows can be reassigned. Manual matches are green; review confirmation is separate.</p>
        {(selected.left.size > 0 || selected.right.size > 0) && <details className="small mt-2"><summary>Selected rows, including hidden rows</summary>
          {['left', 'right'].map(side => <div key={side}><strong>{SIDE_LABELS[side]}</strong>
            {(side === 'left' ? selectedLeft : selectedRight).map(row => <Button key={row.id} size="sm" variant="light" disabled={!editable}
              className="m-1" onClick={() => pick(side, row.id)}>{display(primaryIdentifier(row))} · {display(row.quantity)} · {display(locationLabel(row))} ×</Button>)}</div>)}
        </details>}
      </Card.Body></Card>}
      <p className="small text-muted my-2" aria-live="polite">{independent
        ? 'Independent view: each side filters and pages separately. Rows at the same height are not necessarily matches.'
        : 'Aligned group view: blank rows keep each match group together. Click a part number to search the opposite side.'}</p>
      <div className="audit-panes">
        {['left', 'right'].map(side => <Card key={side} className="audit-pane">
          <Card.Header className={side === 'left' ? 'audit-expected-header' : 'audit-captured-header'}>
            <h3>{SIDE_LABELS[side]}</h3><span>{(side === 'left' ? left : right).length.toLocaleString()} rows</span>
          </Card.Header>
          <SideFilters key={`${side}-${filterReset[side]}`} side={side} rows={side === 'left' ? left : right} filter={filters[side]} counts={counts[side]}
            onChange={value => updateFilter(side, value)} onClear={() => updateFilter(side, emptyFilters())} />
          <div className="audit-scroll" ref={element => { scrollPanes.current[side] = element; }} onScroll={event => {
            const other = scrollPanes.current[side === 'left' ? 'right' : 'left'];
            if (!independent && other && Math.abs(other.scrollTop - event.currentTarget.scrollTop) > 1) {
              other.scrollTop = event.currentTarget.scrollTop;
            }
          }}>
            <AuditTable side={side} entries={shown[side]} selected={selected[side]} onSelect={id => pick(side, id)}
              onOpen={openGroup} onSearch={pn => searchOpposite(side, pn)} readOnly={!editable} />
          </div>
          {independent && <Pager label={`${side} rows`} page={pages[side]} count={sideEntries[side].length} onChange={page => setPages(previous => ({ ...previous, [side]: page }))} />}
        </Card>)}
      </div>
      {!independent && <Pager label="groups" page={pages.group} count={filteredGroups.length} onChange={page => setPages(previous => ({ ...previous, group: page }))} />}
      <details className="audit-help mt-3">
        <summary>Matching rules and export details</summary>
        <p>Candidates are found first by comparing every part identifier on either side: Part Number, MPN, IPN, Secondary PN, SKU, and other labeled part-number aliases. Identifiers ignore case and punctuation; comma-, semicolon-, pipe-, or newline-separated aliases are checked individually. Quantity and location then determine Exact, Combined, Suspected, or Needs Review status. Combined groups support 1:N, N:1, and N:M with a shared identifier and matching totals in a coherent location scope. Ambiguous identifier chains and missing locations require review.</p>
        <p>Unmatching preserves all rows and suppresses automatic rematching of those rows. Manual matching can override that suppression. Confirmed means you reviewed the current group, not that its quantities agree.</p>
        <p>Exports include every row, not just filtered results. Shared group supplements repeat on each expected row: do not sum them as individual allocations. Review backups apply only to the same saved project dataset. Reused final location names select all matching names; paths are displayed for context.</p>
      </details>

      <Modal show={Boolean(activeGroup)} onHide={() => setModalId(null)} size="xl" centered scrollable>
        <Modal.Header closeButton><Modal.Title>{activeGroup ? LABELS[activeGroup.status] : 'Match group'}</Modal.Title></Modal.Header>
        {activeGroup && <><Modal.Body className="parts-audit">
          <p>{activeGroup.reason}</p>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Badge bg={activeGroup.manual ? 'success' : 'primary'}>{activeGroup.manual ? 'Manual / saved allocation' : 'Automatic analysis'}</Badge>
            <Badge bg={activeGroup.confirmed ? 'success' : 'secondary'}>{activeGroup.confirmed ? 'Confirmed' : 'Unconfirmed'}</Badge>
            <span>Expected total: <strong>{display(activeGroup.leftQuantity)}</strong> · Captured total: <strong>{display(activeGroup.rightQuantity)}</strong></span>
          </div>
          <div className="audit-modal-panes">
            {['left', 'right'].map(side => <section key={side}>
              <h4>{SIDE_LABELS[side]} ({activeGroup[side].length})</h4>
              <div className="audit-scroll"><AuditTable side={side} detail readOnly
                entries={activeGroup[`${side}Rows`].map(row => ({ row, group: activeGroup }))}
                onSearch={pn => searchOpposite(side, pn)} /></div>
              <details className="small mt-2"><summary>All source fields</summary>
                {activeGroup[`${side}Rows`].map(row => <div key={row.id} className="audit-source">
                  <strong>{display(primaryIdentifier(row))} · {row.id}</strong>
                  <div className="d-flex flex-wrap gap-2 my-2" aria-label={`Part identifiers for ${row.id}`}>
                    {partIdentifiers(row).map(identifier => <Button key={identifier} size="sm" variant="outline-primary"
                      onClick={() => searchOpposite(side, identifier)}>{identifier}</Button>)}
                  </div>
                  <dl>{Object.entries(row.source).map(([key, value]) => <React.Fragment key={key}><dt>{key}</dt><dd>{typeof value === 'object' ? JSON.stringify(value) : display(value)}</dd></React.Fragment>)}</dl>
                </div>)}
              </details>
            </section>)}
          </div>
          <Form.Label className="mt-3" htmlFor="audit-group-note">Review note</Form.Label>
          <Form.Control as="textarea" rows={2} id="audit-group-note" value={groupNote} disabled={!editable} onChange={event => setNote(event.target.value)} />
          <p className="small text-muted mt-2 mb-0">Unmatch removes the allocation and returns rows to Needs Review. It never deletes inventory. Review confirmation does not change the match.</p>
        </Modal.Body><Modal.Footer>
          {canEdit && <>
            <Button variant="outline-primary" disabled={!editable || groupNote === activeGroup.note} onClick={() => mutate(() => setGroupNote(document, activeGroup, groupNote))}>Save note</Button>
            <Button variant={activeGroup.confirmed ? 'success' : 'outline-success'} disabled={!editable} aria-pressed={activeGroup.confirmed}
              onClick={() => mutate(() => toggleConfirmed(document, activeGroup))}>{activeGroup.confirmed ? 'Confirmed · mark unconfirmed' : 'Confirm review'}</Button>
            <Button variant="outline-danger" disabled={!editable || (!activeGroup.left.length && !activeGroup.right.length)} onClick={() =>
              ask('Unmatch this group?', 'All rows will be preserved and returned to Needs Review. Automatic analysis will not recreate this allocation.', () => mutate(() => {
                const next = unmatchGroup(document, activeGroup); setModalId(null); return next;
              }))}>Unmatch group</Button>
          </>}
          <Button variant="secondary" onClick={() => setModalId(null)}>Close</Button>
        </Modal.Footer></>}
      </Modal>

      <Modal show={Boolean(confirmation)} onHide={() => setConfirmation(null)} centered>
        <Modal.Header closeButton><Modal.Title>{confirmation?.title}</Modal.Title></Modal.Header>
        <Modal.Body>{confirmation?.body}</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setConfirmation(null)}>Cancel</Button>
          <Button variant="primary" disabled={!editable} onClick={() => {
            const action = confirmation.action; setConfirmation(null); action();
          }}>Confirm</Button></Modal.Footer>
      </Modal>
      <Modal show={Boolean(importPreview)} onHide={() => setImportPreview(null)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Preview Expected CSV</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>{importPreview?.name} · {importPreview?.document.lineItems.length} expected rows.</p>
          <Alert variant="warning">Import replaces expected inventory and its audit decisions. Captured project parts are unchanged. Back up or export your current work first.</Alert>
          <div className="table-responsive"><Table size="sm"><thead><tr><th>Part number</th><th>Quantity</th><th>Location</th></tr></thead><tbody>
            {importPreview?.document.lineItems.slice(0, 5).map(item => { const row = expectedRow(item); return <tr key={row.id}><td>{display(primaryIdentifier(row))}</td><td>{display(row.quantity)}</td><td>{display(row.location)}</td></tr>; })}
          </tbody></Table></div><p className="text-muted small">First five rows shown. Original CSV columns are retained for export. Rows with missing identifiers or invalid quantities are kept for review, not rejected.</p>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setImportPreview(null)}>Cancel</Button>
          <Button variant="danger" disabled={!editable} onClick={() => {
            const next = { ...document, ...importPreview.document };
            setImportPreview(null); clearSelection(); clearFilters(); setModalId(null); mutate(() => next);
          }}>Replace expected inventory</Button></Modal.Footer>
      </Modal>
    </Container>
  );
}

export default PartsComparisonTool;
